/**
 * Modal for selecting portfolio view tier (Full user, Tier 1-3, Basic).
 */
import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { loadPortfolioPrefs, savePortfolioPrefs, type ViewTier } from '../../../shared/utils/portfolioPrefs';
import type { PortfolioViewTier, PortfolioViewOption } from '../types';

// localStorage keys for selected account and ticker (must match PortfolioPage)
const LOCALSTORAGE_SELECTED_TICKER = 'dashboard_selectedTicker';
const LOCALSTORAGE_SELECTED_ACCOUNT_ID = 'dashboard_selectedAccountId';

interface ViewPortfolioAsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PORTFOLIO_VIEW_OPTIONS: PortfolioViewOption[] = [
  {
    value: 'full',
    label: 'Full User',
    description: 'See everything',
    color: 'bg-gray-100 text-gray-900',
    starCount: 0,
    starColor: '',
  },
  {
    value: 'tier1',
    label: 'Gold tier (Tier 1)',
    description: 'All %s and charts visible, $ values hidden',
    color: 'bg-yellow-100 text-yellow-900',
    starCount: 1,
    starColor: 'text-yellow-500',
  },
  {
    value: 'tier2',
    label: 'Silver tier (Tier 2)',
    description: 'Portfolio and account %s visibile, individual ticker data hidden',
    color: 'bg-gray-200 text-gray-900',
    starCount: 1,
    starColor: 'text-gray-400',
  },
  {
    value: 'tier3',
    label: 'Bronze tier (Tier 3)',
    description: 'Portfolio %s visibile, account and ticker data hidden',
    color: 'bg-orange-100 text-orange-900',
    starCount: 1,
    starColor: '',
    starCustomColor: '#A75217',
  },
  {
    value: 'basic',
    label: 'Basic Friend',
    description: 'All data hidden',
    color: 'bg-blue-100 text-blue-900',
    starCount: 1,
    starColor: 'text-gray-300',
  },
];

/**
 * Convert PortfolioViewTier ('full' | 'tier1' | ...) to ViewTier ('private' | 'tier1' | ...)
 * Maps 'full' → 'private' for backend compatibility
 */
function portfolioViewTierToViewTier(tier: PortfolioViewTier): ViewTier {
  return tier === 'full' ? 'private' : tier;
}

/**
 * Convert ViewTier ('private' | 'tier1' | ...) to PortfolioViewTier ('full' | 'tier1' | ...)
 * Maps 'private' → 'full' for UI compatibility
 */
function viewTierToPortfolioViewTier(tier: ViewTier): PortfolioViewTier {
  return tier === 'private' ? 'full' : tier;
}

const ViewPortfolioAsModal: React.FC<ViewPortfolioAsModalProps> = ({ isOpen, onClose }) => {
  const [selectedTier, setSelectedTier] = useState<PortfolioViewTier>('full');

  useEffect(() => {
    // Load current selection from portfolioPrefs (synchronized with PortfolioLayout)
    const prefs = loadPortfolioPrefs();
    const portfolioTier = viewTierToPortfolioViewTier(prefs.selfViewTier);
    setSelectedTier(portfolioTier);
  }, []);

  const handleSelect = (tier: PortfolioViewTier) => {
    setSelectedTier(tier);
    
    // Save to portfolioPrefs (synchronized with PortfolioLayout)
    const viewTier = portfolioViewTierToViewTier(tier);
    savePortfolioPrefs({ selfViewTier: viewTier });
    
    // Clear localStorage selections based on tier visibility restrictions:
    // - Bronze (tier3): Can't see account or ticker data → clear both
    // - Silver (tier2): Can't see individual ticker data → clear ticker only
    // - Basic: Can't see anything → clear both (though it doesn't matter)
    // - Full and tier1: Can see everything → don't clear
    if (tier === 'tier3' || tier === 'basic') {
      // Bronze and Basic: Clear both account and ticker selections
      localStorage.removeItem(LOCALSTORAGE_SELECTED_ACCOUNT_ID);
      localStorage.removeItem(LOCALSTORAGE_SELECTED_TICKER);
    } else if (tier === 'tier2') {
      // Silver: Clear ticker selection only (can see accounts but not individual tickers)
      localStorage.removeItem(LOCALSTORAGE_SELECTED_TICKER);
    }
    // Full and tier1: No clearing needed (can see everything)
    
    // Dispatch custom event to notify same-window listeners (storage events only fire cross-tab)
    window.dispatchEvent(new CustomEvent('portfolioPrefsChanged', { detail: { selfViewTier: viewTier } }));
    
    // Close modal after a brief delay to show selection
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="View Portfolio As"
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600 mb-4">
          Select how you want to view your portfolio. This will affect what data is visible.
        </p>
        
        {PORTFOLIO_VIEW_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
              selectedTier === option.value
                ? `${option.color} border-green-500`
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {option.starCount > 0 && (
                  <div className={`text-base flex-shrink-0 ${option.starColor}`} style={option.starCustomColor ? { color: option.starCustomColor } : undefined}>
                    {'★'.repeat(option.starCount)}
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500 mt-1">{option.description}</div>
                </div>
              </div>
              {selectedTier === option.value && (
                <svg
                  className="h-5 w-5 text-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
};

export default ViewPortfolioAsModal;

